import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Check, Minus, FileSpreadsheet } from 'lucide-react';
import { FinancialValue } from '@/components/shared/FinancialValue';
import HandoffLine from '@/components/handoff/HandoffLine';
// Canonical case shape (Phase 0 type unification, 2026-08-22 —
// docs/plans/2026-08-22-accountant-tier1-4-plan.md §1.3 step 4). Replaces the
// local, thinner `AccountantCaseRow` interface that used to live here so
// WorkDesk.tsx no longer needs `as any` to pass its rows through.
import type { CaseSummary } from './types';

interface CaseTableProps {
  cases: CaseSummary[];
  variant: 'queue' | 'signed';
  selectedCaseId: string | null;
  onSelectCase: (id: string) => void;
  /**
   * Phase 2 (D1 toolbar) empty state: when `cases` is empty BECAUSE a
   * search/status filter narrowed it to zero — as opposed to the tab
   * genuinely having no cases — the caller passes `filterEmpty` so this
   * renders a single centered "no match" row (with the table's header intact
   * for context) instead of nothing. When `cases` is empty and `filterEmpty`
   * is false/unset, this renders an empty <tbody> as before — the caller's
   * own "no cases" block (rendered above CaseTable) is what's shown.
   */
  filterEmpty?: boolean;
  /** Clears the active filter/search — only used when `filterEmpty` is true. */
  onClearFilter?: () => void;
  /**
   * Phase 3 (D3 sign-off workflow) — fires when the row's SIGN OFF button is
   * clicked. Per the K3 pixel-pass ruling this OPENS THE RAIL AND LOCUSES THE
   * SIGN-OFF CARD; it does NOT POST. `SignOffReadinessCard` owns the actual
   * POST /api/v1/case-signoff/{id}. Rendered on any row where `canSignOff(row)`
   * holds (below) — WorkDesk supplies this handler.
   */
  onSignOff?: (id: string) => void;
}

// Phase 3 (D3) — ported VERBATIM from AccountantCaseRow.tsx:67-70. Do not
// modify: this predicate is regression-tested (AccountantCaseRow.readiness.
// test.tsx, the 2026-04-30 d6d88aea investigation — case_strength=42,
// assigned_queue='accountant', is_ready_for_review=true must still read as
// ready). `is_ready_for_review` is the canonical SSoT from
// CaseReadinessService.assess_readiness(); case_strength>=70 and
// assigned_queue==='ready' are additive legacy fallbacks, kept as OR terms
// (not a veto) for older API responses that predate the field.
export function canSignOff(row: CaseSummary): boolean {
  return (
    row.is_ready_for_review === true ||
    row.case_strength >= 70 ||
    row.assigned_queue === 'ready'
  );
}

// Phase 3 (D2) — fixed pixel grid per the K3 pixel-pass verdict (D2, "ADJUSTED
// — additive 7-column spec does not fit at 1440"): CASE flexes, the other 4
// are fixed. 96+208+48+148 = 500px fixed + 4×16px gaps = 564px non-flex.
//
// Phase 3-refine (K3 §1, 2026-08-22): READINESS widened 208→232 (chips were
// wrapping to a second line — never scannable at a glance), taken from
// CASE's dead space (240→220). CASE stays the flex column so it still
// absorbs any extra viewport width; only its floor moved.
const GRID_COLS = 'minmax(220px,1fr) 96px 232px 48px 148px';

function formatEventTag(eventType: string | null | undefined): string | null {
  if (!eventType) return null;
  return eventType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// Standard border-box grammar, ported from PaginationBar.tsx's PREV/NEXT
// buttons (D1) — the one squared button style every row action shares.
// Horizontal padding tightened px-2.5 -> px-1.5 as part of the 2026-08-23
// column-fit fix: measured in the harness, RELEASE (7 chars) + SIGN OFF (8
// chars) at px-2.5 summed to ~156.5px — wider than the 148px ACTIONS column
// even in isolation from the hover-icon group below, so shrinking the
// button count to 2 alone was not sufficient; the pair needed to physically
// fit. px-1.5 brings the pair to ~137.5px (+4px gap = ~141.5px), fitting
// with margin. Same class serves CLAIM/OPEN too, so the tightening is
// uniform across every row-action button, not sign-off-specific.
const ROW_BUTTON_CLASS =
  'h-7 px-1.5 inline-flex items-center gap-1 border border-border rounded-[2px] text-[11px] font-medium uppercase tracking-wide text-text-muted hover:text-brand hover:border-brand/40 transition-colors duration-150 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-text-muted disabled:hover:border-border shrink-0';

// Phase 3 (D2) — READINESS cell's DATA/DOCS/CALC micro-chip. K3's blind-D2
// spec ("labeled micro-chips, two-channel state — icon + luminance, one
// hueless hue"), applied to the REAL fields (see the CRITICAL DATA
// CORRECTION in the task spec — the queue row has no docs-verified/recon-pct/
// variance-count; those are per-case DataQualityCard values from a different
// fetch, not on AccountantCaseResponse).
//
// Phase 3-refine (K3 §1, 2026-08-22): three-state, not two. 'ok' (clean) →
// var(--color-verified) (green remapped hueless, per Code-fact #1
// inline-only) — unchanged from the original two-state chip, so the
// existing 2026-05-03 badge-predicate tests still hold. 'miss' (not clean,
// row carries no readiness_issues) stays the original muted/dim treatment —
// not-clean is not automatically a blocker. 'block' (not clean AND the row
// HAS open readiness_issues) is new: crimson, via the same
// --color-blocker-tint/-tint-bd tokens the row's old standalone "N
// BLOCKERS" chip used (that chip is deleted — see StrengthRing's badge,
// which now carries the count). There is no per-dimension mapping from
// readiness_issues text to Data/Docs/Calc specifically (the field is a flat
// string list), so "this chip IS a readiness issue" is expressed as
// "not-clean on a row that has issues" — the nearest honest reading of K3's
// spec without fabricating an issue-to-dimension link that doesn't exist in
// the API contract.
type ReadinessChipVariant = 'ok' | 'miss' | 'block';

function ReadinessChip({ label, variant }: { label: string; variant: ReadinessChipVariant }) {
  const tokens: Record<ReadinessChipVariant, React.CSSProperties> = {
    ok: {
      color: 'var(--color-verified)',
      borderColor: 'var(--color-border)',
      backgroundColor: 'var(--color-bg-elevated)',
    },
    miss: {
      color: 'var(--color-text-dim)',
      borderColor: 'var(--color-border-faint)',
      backgroundColor: 'transparent',
    },
    block: {
      color: 'var(--color-blocker)',
      borderColor: 'var(--color-blocker-tint-bd)',
      backgroundColor: 'var(--color-blocker-tint)',
    },
  };
  return (
    <span
      className="inline-flex items-center gap-0.5 h-5 px-1.5 rounded-[4px] border text-[10px] font-mono font-semibold uppercase tracking-wider shrink-0 whitespace-nowrap"
      style={tokens[variant]}
    >
      {variant === 'ok' ? <Check className="w-2.5 h-2.5" /> : <Minus className="w-2.5 h-2.5" />}
      {label}
    </span>
  );
}

// Phase 3 (D2) — case_strength ring. Port of the SVG math from
// AccountantCaseRow.tsx:246-264. Per the K3 pixel-pass verdict (D2): fill is
// var(--color-verified) — and var(--color-blocker) when the row carries open
// readiness_issues — NEVER orange (the ring is data, not action). Track is
// var(--color-bg-hover) per the pixel-pass (not var(--color-border), which
// the blind spec used).
//
// Phase 3-refine (K3 §1/§3, 2026-08-22): three changes.
// (1) Resized 36px→26px, stroke 3px→2.5px, per K3's exact tokens — the
//     wider READINESS column (see GRID_COLS) was budgeted around this size.
// (2) ALWAYS renders (K3 §3): an unscored row (`pct` null/undefined) used to
//     fall back to a bare "—" glyph at a different x-origin than the ring,
//     which shifted the chip group's left edge row-to-row (Summit vs.
//     Harbor didn't align in K3's render). Now every row gets the same
//     26px ring: track-only (no arc, empty center) when unscored, so the
//     chips always start from the same x-offset. Never orange either way.
// (3) Blocker COUNT badge (K3 §1): replaces the deleted standalone "N
//     BLOCKERS" row chip. Renders whenever the row has open
//     readiness_issues — independent of whether `pct` is scored — because
//     issues can exist outside the Data/Docs/Calc chips' predicates (the
//     case this badge specifically covers per K3: readiness_issues>0 but
//     all three chips read clean).
const RING_SIZE = 26;
const RING_STROKE = 2.5;
const RING_R = RING_SIZE / 2 - RING_STROKE / 2 - 1; // 1px inner padding so the arc never clips the box
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_R;

function StrengthRing({ pct, hasBlockers, issuesCount }: { pct: number | null | undefined; hasBlockers: boolean; issuesCount: number }) {
  const scored = typeof pct === 'number';
  const clamped = scored ? Math.max(0, Math.min(100, pct as number)) : 0;
  const dash = scored ? (clamped / 100) * RING_CIRCUMFERENCE : 0;
  const arcColor = hasBlockers ? 'var(--color-blocker)' : 'var(--color-verified)';
  const threeDigit = scored && clamped >= 100;
  return (
    <div className="relative shrink-0" style={{ width: RING_SIZE, height: RING_SIZE }}>
      <svg
        width={RING_SIZE}
        height={RING_SIZE}
        className="-rotate-90"
        viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
      >
        <circle
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={RING_R}
          fill="none"
          style={{ stroke: 'var(--color-bg-hover)' }}
          strokeWidth={RING_STROKE}
        />
        {scored && (
          <circle
            cx={RING_SIZE / 2}
            cy={RING_SIZE / 2}
            r={RING_R}
            fill="none"
            strokeWidth={RING_STROKE}
            style={{ stroke: arcColor }}
            strokeDasharray={`${dash} ${RING_CIRCUMFERENCE}`}
            strokeLinecap="round"
          />
        )}
      </svg>
      {scored && (
        <span
          className="absolute inset-0 flex items-center justify-center font-mono font-semibold"
          style={{ color: 'var(--color-text-secondary)', fontSize: threeDigit ? '8.5px' : '9.5px' }}
        >
          {clamped}
        </span>
      )}
      {hasBlockers && (
        <span
          className="absolute flex items-center justify-center font-mono font-bold leading-none"
          style={{
            top: -5,
            right: -6,
            height: 13,
            minWidth: 13,
            padding: '0 3px',
            borderRadius: 7,
            background: 'var(--color-blocker)',
            color: 'var(--color-bg-base)',
            fontSize: '8.5px',
          }}
          title={`${issuesCount} readiness blocker${issuesCount !== 1 ? 's' : ''}`}
        >
          {issuesCount}
        </span>
      )}
    </div>
  );
}

function CaseRow({
  row,
  variant,
  isSelected,
  onSelectCase,
  onSignOff,
}: {
  row: CaseSummary;
  variant: 'queue' | 'signed';
  isSelected: boolean;
  onSelectCase: (id: string) => void;
  onSignOff?: (id: string) => void;
}) {
  const navigate = useNavigate();
  const hasBlockers = (row.readiness_issues?.length || 0) > 0;
  const issuesCount = row.readiness_issues?.length || 0;
  const pending = row.pending_classifications || 0;
  const eventTag = formatEventTag(row.event_type);
  const id8 = row.id ? row.id.slice(0, 8) : null;

  // Phase 3 (D2) READINESS badges — ported predicates from
  // AccountantCaseRow.tsx:198-243 (2026-05-03 follow-up: `undefined` fields
  // must NOT read as dirty — both are defensively `?? 0`). PREDICATES ARE
  // UNCHANGED by the Phase 3-refine pass — only the chip's visual variant
  // (ok/miss/block) is new, layered on top per K3 §1.
  const dataClean = (row.transactions_need_review ?? 0) === 0 && (row.anomalies_count ?? 0) === 0;
  const docsClean = row.is_ready_for_review === true;
  const calcClean = !!row.is_ready_for_review;
  const chipVariant = (clean: boolean): ReadinessChipVariant => (clean ? 'ok' : hasBlockers ? 'block' : 'miss');

  // 2026-10-24 (Phase 1a): the claim/pool model is retired — the workdesk shows one
  // unified case list and SIGN OFF is available on any eligible row, not just claimed
  // ones (the backend already allows sign-off unclaimed — case_signoff.py:102).
  const showRowSignOff = !!onSignOff && canSignOff(row);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      if (e.key === ' ') e.preventDefault();
      onSelectCase(row.id);
    }
  };

  return (
    <div
      role="row"
      data-testid={`case-row-${row.id}`}
      aria-selected={isSelected}
      tabIndex={0}
      onClick={() => onSelectCase(row.id)}
      onKeyDown={handleKeyDown}
      className="group grid items-center gap-4 px-3 cursor-pointer focus:outline-none focus-visible:ring-1 focus-visible:ring-brand focus-visible:ring-inset hover:bg-[var(--color-bg-hover)] border-b border-border-faint"
      style={{
        gridTemplateColumns: GRID_COLS,
        // Phase 3-refine (K3 §2, 2026-08-22): row height raised to a 68px
        // floor (K3's "44px content + 12px block padding", up from the
        // previous 64px). Still `minHeight`, NOT a hard `height`/
        // `gridAutoRows` cap, as a safety margin — even though the
        // ACTIONS cell now renders a single button (SIGN OFF or OPEN), which
        // fits the 148px column on one line. `minHeight`
        // is kept rather than a hard cap so any future column content still
        // grows safely downward instead of clipping/overlapping, per the
        // original overlap bug this comment (see git history) was written
        // to prevent.
        minHeight: 68,
        backgroundColor: isSelected ? 'var(--color-bg-hover)' : undefined,
        borderLeft: `2px solid ${isSelected ? 'var(--color-verified)' : 'transparent'}`,
      }}
    >
      {/* CASE — L1 15px/20, L2 12px/16, both truncate to one line, never
          wrap (K3 §2 "lock it"). */}
      <div className="min-w-0 text-left">
        <div
          className="truncate font-medium"
          style={{ color: 'var(--color-text-primary)', fontSize: '15px', lineHeight: '20px' }}
          title={row.business_name}
        >
          {row.business_name}
        </div>
        <div className="mt-0.5 min-w-0">
          <HandoffLine handoff={row.handoff} viewerSeat="accountant" />
        </div>
        <div className="flex items-center gap-1 min-w-0 mt-0.5" style={{ fontSize: '12px', lineHeight: '16px' }}>
          {/* Firm name — a lookup key, raised to text-secondary per K3 §P2
              (was text-dim, under-contrast for an identifier attorneys scan
              by). */}
          <span className="truncate" style={{ color: 'var(--color-text-secondary)' }}>
            {row.law_firm || '—'}
          </span>
          {id8 && (
            <span
              className="hidden xl:inline shrink-0 font-mono text-[10px]"
              style={{ color: 'var(--color-text-dim)' }}
              title={row.id}
            >
              · {id8}
            </span>
          )}
          {eventTag && (
            // Phase 3-refine (K3 §P2, 2026-08-22): demoted from an inline
            // "· FIRE" label to a neutral bordered micro-chip. Orange is
            // wayfinding/action only (active tab, hover, focus) — an EVENT
            // tag is data, so it was already off var(--color-text-muted)
            // here (not brand orange) before this pass; the chip form is
            // the requested visual restructuring, same non-brand color.
            <span
              className="shrink-0 font-mono uppercase"
              style={{
                fontSize: '10px',
                letterSpacing: '0.06em',
                padding: '1px 5px',
                borderRadius: 2,
                border: '1px solid var(--color-border)',
                color: 'var(--color-text-muted)',
              }}
            >
              {eventTag}
            </span>
          )}
        </div>
      </div>

      {/* NET CLAIM */}
      <div className="text-right font-mono tabular-nums text-sm" style={{ color: 'var(--color-text-primary)' }}>
        <div className="flex items-center justify-end gap-1.5">
          <FinancialValue value={row.net_supported_claim ?? row.estimated_value} />
          {row.net_supported_claim == null && (
            <span className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--color-text-dim)' }}>est.</span>
          )}
        </div>
      </div>

      {/* READINESS — real-field substitution (see CaseTable header comment).
          Phase 3-refine (K3 §1/§2/§3, 2026-08-22): rebuilt as a STRICTLY
          ONE-LINE cell — ring + 3 chips, never wraps, never grows a second
          line. The standalone crimson "N BLOCKERS" chip that used to render
          below is DELETED: its signal now lives on the ring as a count
          badge (StrengthRing's `hasBlockers`/`issuesCount` props) — see
          K3's rationale (chips wrapping turned a scannable signal into a
          paragraph). The triage rail's own blocker chip is untouched; this
          only removes the row-level duplicate. The +N PENDING CLASS. line
          also can't survive as a second line any more; it moves to a
          `title` tooltip on the cell so the information isn't lost, just
          no longer forced onto the single scan line. */}
      <div
        className="flex items-center gap-2 min-w-0"
        style={{ flexWrap: 'nowrap' }}
        title={pending > 0 ? `+${pending} pending classification${pending !== 1 ? 's' : ''}` : undefined}
      >
        <StrengthRing
          pct={typeof row.case_strength === 'number' ? row.case_strength : null}
          hasBlockers={hasBlockers}
          issuesCount={issuesCount}
        />
        <div className="flex items-center gap-1 min-w-0" style={{ flexWrap: 'nowrap', overflow: 'hidden' }}>
          <ReadinessChip label="Data" variant={chipVariant(dataClean)} />
          <ReadinessChip label="Docs" variant={chipVariant(docsClean)} />
          <ReadinessChip label="Calc" variant={chipVariant(calcClean)} />
        </div>
      </div>

      {/* AGE */}
      <div className="text-right font-mono text-xs" style={{ color: 'var(--color-text-muted)' }}>
        {row.days_stale ?? 0}d
      </div>

      {/* ACTIONS */}
      <div className="flex flex-wrap items-center justify-end gap-1">
        {/* Hover-reveal quick-launch icons — always rendered (hardcoded
            Clerk-gated paths, same precedent as AccountantCaseRow.tsx:298-313
            "P&L Workbench — always shown for in-house staff"; there is no
            per-row URL field backing either target). `invisible` (not a
            width-0/hidden) so the slot is always reserved — no layout shift
            on hover. No transition on the visibility toggle itself (instant
            appear is not motion); the icon's own color hover may transition
            (the lawful action affordance).
            Column-fit fix (2026-08-23): grouped into their OWN nested flex
            div, sibling to the button group below, instead of being loose
            children of the outer flex-wrap row. Loose children meant the 2
            invisible-but-space-reserving icons competed with RELEASE for
            the SAME wrap line — depending on button padding, RELEASE could
            end up sharing a line with the icons while its partner button
            (OPEN/SIGN OFF) got pushed to its own line below, splitting the
            two action buttons apart even though only 2 render now. Grouping
            means the outer row only ever decides between TWO atomic units
            (icon group, button group) — if both fit on one line they share
            it, and if not, the ENTIRE button group (never split) wraps
            below the icon group. Visually identical either way, since the
            icon group is invisible at rest. */}
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/dashboard/accountant/workbench/${row.id}`);
            }}
            aria-label={`Open P&L Workbench for ${row.business_name}`}
            title="P&L Workbench"
            className="invisible group-hover:visible group-focus-within:visible h-7 w-7 flex items-center justify-center rounded-[2px] text-text-dim hover:text-brand transition-colors duration-150 focus:outline-none focus-visible:ring-1 focus-visible:ring-brand"
          >
            <FileSpreadsheet className="w-3.5 h-3.5" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/dashboard/accountant/anomalies/${row.id}`);
            }}
            aria-label={`Open Anomaly Review for ${row.business_name}`}
            title="Anomaly Review"
            className="invisible group-hover:visible group-focus-within:visible h-7 w-7 flex items-center justify-center rounded-[2px] text-text-dim hover:text-brand transition-colors duration-150 focus:outline-none focus-visible:ring-1 focus-visible:ring-brand"
          >
            <AlertTriangle className="w-3.5 h-3.5" aria-hidden="true" />
          </button>
        </div>

        {/* Button group — SIGN OFF when eligible, else OPEN. The claim/pool model
            is retired (Phase 1a, 2026-10-24): no CLAIM / RELEASE. `handleRowSignOff`
            in WorkDesk does everything OPEN does (selects the case / opens the rail)
            PLUS scrolls-to + focuses the sign-off card, so SIGN OFF is a strict
            superset of OPEN for eligible rows. `flex-nowrap` keeps the one button
            on a single line. */}
        <div className="flex items-center flex-nowrap gap-1 shrink-0">
          {showRowSignOff ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onSignOff!(row.id);
              }}
              data-testid={`signoff-${row.id}`}
              className={ROW_BUTTON_CLASS}
            >
              SIGN OFF
            </button>
          ) : (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onSelectCase(row.id);
              }}
              aria-label={`Open ${row.business_name || 'case'}`}
              className={ROW_BUTTON_CLASS}
            >
              OPEN
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function CaseTable({
  cases,
  variant,
  selectedCaseId,
  onSelectCase,
  filterEmpty,
  onClearFilter,
  onSignOff,
}: CaseTableProps) {
  return (
    <div className="overflow-x-auto">
      <div role="table" aria-label="Cases" className="w-full text-sm">
        <div
          role="row"
          className="grid items-center gap-4 px-3 py-2 border-b border-border"
          style={{ gridTemplateColumns: GRID_COLS }}
        >
          <div role="columnheader" className="text-[10px] uppercase tracking-[0.12em] text-text-dim font-medium text-left">
            CASE
          </div>
          <div role="columnheader" className="text-[10px] uppercase tracking-[0.12em] text-text-dim font-medium text-right">
            NET CLAIM
          </div>
          <div role="columnheader" className="text-[10px] uppercase tracking-[0.12em] text-text-dim font-medium text-left">
            READINESS
          </div>
          <div role="columnheader" className="text-[10px] uppercase tracking-[0.12em] text-text-dim font-medium text-right">
            AGE
          </div>
          <div role="columnheader" className="text-[10px] uppercase tracking-[0.12em] text-text-dim font-medium text-right" />
        </div>

        <div role="rowgroup">
          {cases.length === 0 && filterEmpty ? (
            // Phase 2 (D1) empty state — em-dash grammar per the design spec.
            <div role="row" className="grid" style={{ gridTemplateColumns: GRID_COLS }}>
              <div style={{ gridColumn: '1 / -1' }} className="py-10 text-center">
                <span className="text-sm text-text-dim">— NO CASES MATCH THIS FILTER</span>{' '}
                {onClearFilter && (
                  <button
                    type="button"
                    onClick={onClearFilter}
                    className="text-brand hover:underline transition-colors duration-150"
                  >
                    CLEAR FILTER
                  </button>
                )}
              </div>
            </div>
          ) : (
            cases.map((row) => (
              <CaseRow
                key={row.id}
                row={row}
                variant={variant}
                isSelected={row.id === selectedCaseId}
                onSelectCase={onSelectCase}
                onSignOff={onSignOff}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}
